import React, { useState, useEffect, useCallback } from "react";
import styles from "./CustomerList.module.sass";
import cn from "classnames";
import { useNavigate } from "react-router-dom"; 
import Card from "../../components/Card";
import Form from "../../components/Form";
import Table from "./Table";
import Icon from "../../components/Icon";
import { getStudentList } from "../../services/student.service";

const StudentList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectetudent, setSelectedStudent] = useState(null);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      const options = {
        page: currentPage,
        perPage: 10,
        sort: "-created",
      };

      if (search.trim()) {
        options.filter = `first_name ~ "${search}" || last_name ~ "${search}" || email ~ "${search}"`;
      }

      const result = await getStudentList(options);

      setStudents(result.items || []);
      setTotalPages(result.totalPages || 1);
      setNoResults(result.items.length === 0 && !!search);
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des étudiants:", err);
        setError("Impossible de charger les étudiants. Veuillez réessayer.");
        setStudents([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [search, currentPage]);
  
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    fetchStudents();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStudentSelect = (id) => {
    const student = students.find((student) => student.id === id);
    if (student) {
      setSelectedStudent(student);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Élèves"
        classTitle={cn("title-purple", styles.title)}
        classCardHead={styles.head}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par nom, prénom ou email"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/students/create")}>Créer un élève</button>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement des élèves...</div>
        ) : (
          <div className={styles.row}>
            {noResults && students.length === 0 && (
              <div className={styles.empty}>
                Aucun élève trouvé pour votre recherche "{search}".
              </div>
            )}
            {!noResults && students.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucun élève disponible.
              </div>
            )}
            {students.length > 0 && (
              <>
                <Table
                  className={styles.table}
                  items={students}
                  onStudentSelect={handleStudentSelect}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />

                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                    disabled={currentPage === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
};

export default StudentList;